// src/pages/buyer/RfqDetailPage.jsx
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rfqApi, addressApi } from '../../api/services'
import { Button, Card, CardHeader, CardBody, StatusBadge, PageSpinner, Badge } from '../../components/common/ui'
import { Award, ShoppingCart, FileDown, ArrowLeft, Clock, Package, MessageSquare, CheckCircle, XCircle, Star, ChevronDown, ChevronUp } from 'lucide-react'
import toast from 'react-hot-toast'

export default function RfqDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [convertAddressId, setConvertAddressId] = useState(null)
  const [showConvert, setShowConvert] = useState(false)
  const [counterQuoteId, setCounterQuoteId] = useState(null)
  const [counterForm, setCounterForm] = useState({ price_per_metre: '', lead_time: '', notes: '' })
  const [compareMode, setCompareMode] = useState(false)
  const [selectedQuotes, setSelectedQuotes] = useState([])

  const { data, isLoading } = useQuery({
    queryKey: ['rfq', id],
    queryFn: () => rfqApi.buyerShow(id).then(r => r.data?.data || r.data),
  })

  const { data: addrData } = useQuery({
    queryKey: ['addresses'],
    queryFn: () => addressApi.list(),
    enabled: showConvert,
  })

  const awardMutation = useMutation({
    mutationFn: (quoteId) => rfqApi.award(id, quoteId),
    onSuccess: () => { toast.success('Quote awarded!'); qc.invalidateQueries({ queryKey: ['rfq', id] }) },
    onError: e => toast.error(e.response?.data?.message || 'Failed to award'),
  })

  const convertMutation = useMutation({
    mutationFn: () => rfqApi.convertToOrder(id, { address_id: convertAddressId }),
    onSuccess: (res) => {
      const order = res?.data?.data
      toast.success('Order created from quotation!')
      navigate(`/buyer/orders/${order?.id || ''}`)
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to convert'),
  })

  const counterMutation = useMutation({
    mutationFn: ({ quoteId, data }) => rfqApi.counterOffer(id, quoteId, data),
    onSuccess: () => {
      toast.success('Counter-offer sent!')
      setCounterQuoteId(null)
      setCounterForm({ price_per_metre: '', lead_time: '', notes: '' })
      qc.invalidateQueries({ queryKey: ['rfq', id] })
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to send counter-offer'),
  })

  const downloadPdf = async () => {
    try {
      const res = await rfqApi.quotationPdf(id)
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `quotation-${data?.rfq_number || id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download PDF')
    }
  }

  if (isLoading) return <PageSpinner />
  const rfq = data

  const quotes = rfq?.quotes || []
  const awarded = rfq?.awarded_quote || rfq?.awardedQuote
  const addresses = addrData?.data?.data || []

  // Active quotes (non-revised)
  const activeQuotes = quotes.filter(q => q.status !== 'revised')
  const bestPrice = activeQuotes.length > 0 ? Math.min(...activeQuotes.map(q => Number(q.price_per_metre))) : null
  const avgPrice = activeQuotes.length > 0 ? activeQuotes.reduce((s, q) => s + Number(q.price_per_metre), 0) / activeQuotes.length : null

  // Compare mode
  const toggleCompareQuote = (qId) => {
    setSelectedQuotes(prev =>
      prev.includes(qId) ? prev.filter(x => x !== qId) : prev.length < 3 ? [...prev, qId] : prev
    )
  }
  const compareQuotes = quotes.filter(q => selectedQuotes.includes(q.id))

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate('/buyer/rfq')} className="flex items-center gap-1 text-sm text-muted hover:text-pink mb-4">
        <ArrowLeft size={14} /> Back to RFQs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-xl font-display font-bold flex items-center gap-2">
            {rfq?.title} <StatusBadge status={rfq?.status} />
          </h1>
          <p className="text-sm text-muted mt-1">{rfq?.rfq_number}</p>
        </div>
        <div className="flex gap-2">
          {activeQuotes.length >= 2 && (
            <Button variant={compareMode ? 'primary' : 'secondary'} size="sm" onClick={() => { setCompareMode(!compareMode); setSelectedQuotes([]) }}>
              {compareMode ? 'Exit Compare' : 'Compare Quotes'}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={downloadPdf}>
            <FileDown size={14} /> PDF
          </Button>
        </div>
      </div>

      {/* Requirements */}
      <Card className="mb-6">
        <CardHeader><span className="font-bold text-sm">Requirements</span></CardHeader>
        <CardBody>
          <p className="text-sm text-gray-700 whitespace-pre-wrap mb-4">{rfq?.description}</p>
          <div className="grid sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-surface rounded-xl p-3">
              <p className="text-[10px] font-bold text-muted uppercase">Quantity</p>
              <p className="font-bold">{rfq?.quantity_metres}m</p>
            </div>
            {rfq?.fabric_type && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">Fabric</p>
                <p className="font-bold capitalize">{rfq.fabric_type}</p>
              </div>
            )}
            {rfq?.target_price_per_metre && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">Target Price</p>
                <p className="font-bold">₹{rfq.target_price_per_metre}/m</p>
              </div>
            )}
            {rfq?.gsm_range && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">GSM</p>
                <p className="font-bold">{rfq.gsm_range}</p>
              </div>
            )}
            {rfq?.colour_preference && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">Colour</p>
                <p className="font-bold">{rfq.colour_preference}</p>
              </div>
            )}
            {rfq?.weave_type && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">Weave</p>
                <p className="font-bold capitalize">{rfq.weave_type}</p>
              </div>
            )}
            {rfq?.deadline && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">Deadline</p>
                <p className="font-bold flex items-center gap-1"><Clock size={12} /> {new Date(rfq.deadline).toLocaleDateString('en-IN')}</p>
              </div>
            )}
            {rfq?.delivery_state && (
              <div className="bg-surface rounded-xl p-3">
                <p className="text-[10px] font-bold text-muted uppercase">Delivery</p>
                <p className="font-bold">{rfq.delivery_state}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Quote Summary Bar */}
      {activeQuotes.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
            <p className="text-lg font-extrabold text-green-700">₹{bestPrice?.toLocaleString('en-IN')}/m</p>
            <p className="text-[10px] text-green-600 font-bold uppercase">Best Price</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
            <p className="text-lg font-extrabold text-blue-700">₹{Math.round(avgPrice)?.toLocaleString('en-IN')}/m</p>
            <p className="text-[10px] text-blue-600 font-bold uppercase">Avg Price</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
            <p className="text-lg font-extrabold text-purple-700">{activeQuotes.length}</p>
            <p className="text-[10px] text-purple-600 font-bold uppercase">Quotes</p>
          </div>
        </div>
      )}

      {/* Compare Mode Side-by-Side */}
      {compareMode && selectedQuotes.length >= 2 && (
        <Card className="mb-6 border-blue-200">
          <CardHeader className="bg-blue-50"><span className="font-bold text-sm text-blue-800">Quote Comparison</span></CardHeader>
          <CardBody className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface">
                  <th className="text-left px-4 py-3 text-xs font-bold text-muted uppercase w-32">Attribute</th>
                  {compareQuotes.map(q => (
                    <th key={q.id} className="text-center px-4 py-3 text-xs font-bold uppercase">
                      {q.supplier?.business_name || q.supplier?.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Price/m', render: q => `₹${Number(q.price_per_metre).toLocaleString('en-IN')}`, highlight: true },
                  { label: 'Total', render: q => `₹${Number(q.total_quote).toLocaleString('en-IN')}` },
                  { label: 'Lead Time', render: q => q.lead_time || '—' },
                  { label: 'Min Qty', render: q => q.minimum_quantity || '—' },
                  { label: 'Status', render: q => q.status },
                  { label: 'Notes', render: q => q.notes || '—' },
                  { label: 'Revision', render: q => q.revision_number > 1 ? `Rev #${q.revision_number}` : 'Original' },
                ].map(row => (
                  <tr key={row.label} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 text-xs font-bold text-muted">{row.label}</td>
                    {compareQuotes.map(q => {
                      const val = row.render(q)
                      const isBest = row.highlight && Number(q.price_per_metre) === bestPrice
                      return (
                        <td key={q.id} className={`px-4 py-3 text-center ${isBest ? 'text-green-700 font-extrabold bg-green-50' : ''}`}>
                          {val}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {/* Quotes List */}
      <Card className="mb-6">
        <CardHeader>
          <span className="font-bold text-sm">Quotes ({quotes.length})</span>
          {compareMode && <span className="text-xs text-blue-600 ml-2">Select 2-3 quotes to compare</span>}
        </CardHeader>
        <CardBody className="p-0">
          {quotes.length === 0 ? (
            <p className="text-sm text-muted text-center py-8">No quotes yet — suppliers will respond soon.</p>
          ) : (
            <div className="divide-y divide-border">
              {quotes.map(q => {
                const isBest = Number(q.price_per_metre) === bestPrice && q.status !== 'revised'
                const isSelected = selectedQuotes.includes(q.id)
                return (
                  <div key={q.id} className={`p-4 transition-colors ${q.is_awarded ? 'bg-green-50' : q.status === 'revised' ? 'bg-gray-50 opacity-60' : isBest ? 'bg-green-50/30' : 'hover:bg-surface/50'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        {compareMode && q.status !== 'revised' && (
                          <input type="checkbox" checked={isSelected} onChange={() => toggleCompareQuote(q.id)}
                            className="mt-1 accent-pink" disabled={!isSelected && selectedQuotes.length >= 3} />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm">{q.supplier?.business_name || q.supplier?.name}</span>
                            <StatusBadge status={q.status} />
                            {isBest && <Badge className="bg-green-100 text-green-700 text-[10px]">Best Price</Badge>}
                            {q.is_awarded && <Badge className="bg-green-100 text-green-800 text-[10px]">Awarded</Badge>}
                            {q.revision_number > 1 && (
                              <span className="text-[10px] text-blue-500 font-medium">Rev #{q.revision_number} ({q.revision_by})</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-muted text-xs">Price: </span>
                              <span className="font-extrabold">₹{Number(q.price_per_metre).toLocaleString('en-IN')}/m</span>
                            </div>
                            <div>
                              <span className="text-muted text-xs">Total: </span>
                              <span className="font-bold">₹{Number(q.total_quote).toLocaleString('en-IN')}</span>
                            </div>
                            {q.lead_time && (
                              <div>
                                <span className="text-muted text-xs">Lead: </span>
                                <span>{q.lead_time}</span>
                              </div>
                            )}
                            {q.minimum_quantity && (
                              <div>
                                <span className="text-muted text-xs">MOQ: </span>
                                <span>{q.minimum_quantity}</span>
                              </div>
                            )}
                          </div>
                          {q.notes && <p className="text-xs text-gray-500 mt-2">{q.notes}</p>}

                          {/* Price position bar */}
                          {rfq?.target_price_per_metre && q.status !== 'revised' && (
                            <div className="mt-2">
                              <div className="flex items-center gap-2 text-[10px] text-muted">
                                <span>Target</span>
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full relative">
                                  {(() => {
                                    const target = Number(rfq.target_price_per_metre)
                                    const price = Number(q.price_per_metre)
                                    const pct = Math.min(Math.max((price / target) * 50, 5), 95)
                                    const color = price <= target ? 'bg-green-500' : price <= target * 1.2 ? 'bg-amber-500' : 'bg-red-500'
                                    return (
                                      <>
                                        <div className="absolute top-0 left-1/2 w-0.5 h-1.5 bg-gray-600 -translate-x-1/2" />
                                        <div className={`absolute top-0 h-1.5 w-2 rounded-full ${color}`} style={{ left: `${pct}%`, transform: 'translateX(-50%)' }} />
                                      </>
                                    )
                                  })()}
                                </div>
                                <span>2x</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {!compareMode && (
                        <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
                          {rfq?.status === 'open' && !awarded && q.status !== 'revised' && (
                            <>
                              <Button size="sm" loading={awardMutation.isPending} onClick={() => awardMutation.mutate(q.id)}>
                                <Award size={12} /> Award
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setCounterQuoteId(counterQuoteId === q.id ? null : q.id)}>
                                <MessageSquare size={12} /> Counter
                              </Button>
                            </>
                          )}
                          {q.status === 'revised' && <span className="text-gray-400 text-xs">Superseded</span>}
                        </div>
                      )}
                    </div>

                    {/* Counter-offer form */}
                    {counterQuoteId === q.id && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-xl space-y-2">
                        <p className="text-xs font-bold text-gray-700">Send Counter-Offer</p>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <input type="number" step="0.01" placeholder="Your price/m *"
                            value={counterForm.price_per_metre}
                            onChange={e => setCounterForm(f => ({ ...f, price_per_metre: e.target.value }))}
                            className="input text-sm" />
                          <input placeholder="Lead time (optional)"
                            value={counterForm.lead_time}
                            onChange={e => setCounterForm(f => ({ ...f, lead_time: e.target.value }))}
                            className="input text-sm" />
                        </div>
                        <textarea placeholder="Notes..." rows={2}
                          value={counterForm.notes}
                          onChange={e => setCounterForm(f => ({ ...f, notes: e.target.value }))}
                          className="input w-full text-sm" />
                        <div className="flex gap-2">
                          <Button size="sm" loading={counterMutation.isPending}
                            disabled={!counterForm.price_per_metre}
                            onClick={() => counterMutation.mutate({ quoteId: q.id, data: counterForm })}>
                            Send Counter-Offer
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setCounterQuoteId(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Convert to Order */}
      {awarded && rfq?.status === 'awarded' && !awarded.resulting_order_id && (
        <Card className="mb-6 border-green-200 bg-green-50">
          <CardBody>
            <div className="flex items-start gap-3">
              <Package size={22} className="text-green-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-sm text-green-800 mb-1">Ready to Order</h3>
                <p className="text-xs text-green-700 mb-3">
                  Quote awarded to <strong>{awarded.supplier?.business_name || awarded.supplier?.name}</strong> at
                  ₹{Number(awarded.price_per_metre).toLocaleString('en-IN')}/m
                  (Total: ₹{Number(awarded.total_quote).toLocaleString('en-IN')}).
                  Convert this quotation into a purchase order.
                </p>
                {!showConvert ? (
                  <Button size="sm" onClick={() => setShowConvert(true)}>
                    <ShoppingCart size={14} /> Convert to Order
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-green-800">Select delivery address:</label>
                    {addresses.length === 0 ? (
                      <p className="text-xs text-muted">No addresses found. <a href="/buyer/addresses" className="text-pink underline">Add one</a></p>
                    ) : (
                      <div className="space-y-2">
                        {addresses.map(a => (
                          <button key={a.id} onClick={() => setConvertAddressId(a.id)}
                            className={`w-full text-left p-3 rounded-xl border text-xs transition-all ${convertAddressId === a.id ? 'border-green-500 bg-green-100' : 'border-border bg-white hover:border-green-400'}`}>
                            <span className="font-bold">{a.full_name}</span> · {a.address_line1}, {a.city}, {a.state} – {a.pincode}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button size="sm" loading={convertMutation.isPending} disabled={!convertAddressId}
                        onClick={() => convertMutation.mutate()}>
                        Confirm & Create Order
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowConvert(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {awarded?.resulting_order_id && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardBody className="flex items-center gap-3">
            <ShoppingCart size={20} className="text-blue-600" />
            <div>
              <p className="text-sm font-bold text-blue-800">Order Created</p>
              <p className="text-xs text-blue-600">This quotation has been converted into an order.</p>
            </div>
            <Button size="sm" variant="secondary" className="ml-auto"
              onClick={() => navigate(`/buyer/orders/${awarded.resulting_order_id}`)}>
              View Order →
            </Button>
          </CardBody>
        </Card>
      )}

      {rfq?.contact_name && (
        <p className="text-xs text-muted bg-surface rounded-xl px-4 py-3">
          Contact: {rfq.contact_name} · {rfq.contact_phone} {rfq.company_name ? `· ${rfq.company_name}` : ''}
        </p>
      )}
    </div>
  )
}
